import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ACTIONPLAN, COACHING, GAMEPLAN } from '../../constants';
import { CoachingEntityTypes, CoachingTabs } from '../../utils/typeConstants';
import { selectCurrentEmpid } from '../../pages/login/authSlice';
import { LoadingSpinner } from '../loadingSpinner/loadingspinner';
import './ToDoModal.scss';
import { useGetDueToDosQuery } from '../../pages/homePage/homePageSlice';
import arrow1 from '../../assets/arrow1.png';
import arrow2 from '../../assets/arrow2.png';
import bullseye from '../../assets/Bullseye.png';

interface ToDoListBoxProps {
    type: 'mytodos' | 'myteam';
    via?: string;
}

interface ToDoItem {
    date: string;
    day: string;
    title: string;
    status: string;
    link?: string;
    description?: string;
    coachingEntityId?: number | null;
    coachingEntityTypeId?: number | null;
    dueDate?: string;
    employeeName?: string;
    entityDate?: string;
    coachingToDoId?: string;
}

export const ToDoListBox: React.FC<ToDoListBoxProps> = ({ type, via }) => {
    const navigate = useNavigate();
    const empNum = useSelector(selectCurrentEmpid);
    const [toDoList, setToDoList] = useState<ToDoItem[]>([]);
    const { data: myToDoData, isFetching: toDosFetching } = useGetDueToDosQuery({
        employeeNumber: empNum,
        teamType: type,
    });

    useEffect(() => {
        if (myToDoData?.data?.length > 0) {
            const mappedTodos = myToDoData.data.map((item: any) => {
                const dateObj = new Date(item.due_date);
                return {
                    date: dateObj.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
                    day: dateObj.toLocaleDateString('en-US', { weekday: 'short' }),
                    title: item.to_do_title,
                    status: item.coaching_to_do_status_name,
                    link: item.link_url || undefined,
                    description: item.to_do_description || '',
                    coachingEntityId: item.coaching_entity_id ?? null,
                    coachingEntityTypeId: item.coaching_entity_type_id ?? null,
                    dueDate: item.due_date || '',
                    employeeName: item.employee_name || '',
                    entityDate: item.entity_date || '',
                    coachingToDoId: item.coaching_to_do_id || '',
                };
            });

            setToDoList(mappedTodos);
        } else {
            setToDoList([]);
        }
    }, [myToDoData]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Needs Help':
                return 'needs-help';
            case 'To Do':
                return 'todo';
            case 'In Progress':
                return 'in-progress';
            default:
                return '';
        }
    };

    const getEntityTypeName = (typeId?: number | null) => {
        if (!typeId) return '';
        if (typeId === CoachingEntityTypes.Conversations) return 'Discussions';
        const match = Object.entries(CoachingEntityTypes).find(([, value]) => value === typeId);
        return match ? match[0].replace(/([A-Z])/g, ' $1').trim() : '';
    };

    const getNavigationTab = (entityTypeId?: number | null): number => {
        switch (entityTypeId) {
            case CoachingEntityTypes.General:
                return CoachingTabs.MyTODos;
            case CoachingEntityTypes.Conversations:
                return CoachingTabs.Conversation;
            case CoachingEntityTypes.CoachingObservations:
                return CoachingTabs.CoachingObservations;
            case CoachingEntityTypes.CoachingInsights:
                return CoachingTabs.CoachingInsights;
            case CoachingEntityTypes.CorrectiveActions:
                return CoachingTabs.CorrectiveActions;
            case CoachingEntityTypes.Gameplan:
                return CoachingTabs.GamePlan;
            case CoachingEntityTypes.Strengths:
            case CoachingEntityTypes.Metrics:
                return CoachingTabs.Coaching;
            case CoachingEntityTypes.Goals:
                return CoachingTabs.CorrectiveActions;
            default:
                return CoachingTabs.MyTODos;
        }
    };

    const isDueToday = (dateStr: string) => {
        const today = new Date();
        const itemDate = new Date(dateStr);
        return (
            today.getFullYear() === itemDate.getFullYear() &&
            today.getMonth() === itemDate.getMonth() &&
            today.getDate() === itemDate.getDate()
        );
    };

    const handleCreate = () => {
        navigate(`${COACHING}/${CoachingTabs.MyTODos}/1`, { replace: true });
    };
    const handleNavigation = (item: any) => {
        if (item.coachingEntityTypeId === 14 && via === 'agentLandingPage') {
            navigate(`${ACTIONPLAN}/0/${item.coachingEntityId}`, { replace: true });
        }
        else {
            navigate(`${COACHING}/${CoachingTabs.MyTODos}/${type === 'myteam' ? 2 : 1}/${item.coachingToDoId || 1
                }/${item.entityDate || 1}`, { replace: true }
            )
        }
    };
    const renderToDos = () =>
        toDoList.map((item, index) => (
            <div
                key={index}
                className="todo-item"
                role="button"
                tabIndex={0}
                onClick={() => handleNavigation(item)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNavigation(item);
                    }
                }}
            >
                <div
                    className="date-box"
                    style={{ backgroundColor: index % 2 === 0 ? '#E1F9EE' : '#D5F2F9' }}
                >
                    <div className="month">{item.date.split(' ')[1]}</div>
                    <div className="day-number">{item.date.split(' ')[0]}</div>
                    <div className="day-name">{item.day}</div>
                </div>
                <div className="content">
                    <div className="title" style={{ fontWeight: 900 }} title={item.title}>
                        {item.title}
                    </div>
                    <div className="status-badge-wrapper">
                        <span className={`status-badge ${getStatusColor(item.status)}`}>{item.status}</span>
                    </div>
                    {item.dueDate && isDueToday(item.dueDate) && (
                        <div className="due-today">
                            <i className="pi pi-clock" />
                            <span>Due Today</span>
                        </div>
                    )}
                    {type === 'mytodos' ? (
                        <div className="description" title={item.description}>
                            {item.description}
                        </div>
                    ) : (
                        <div className="description" title={item.employeeName}>
                            {item.employeeName}
                        </div>
                    )}
                    {via !== 'agentLandingPage' ? (
                        <div className="extra-content">
                            <div className="icon-circle">
                                <i className="pi pi-arrow-right" />
                            </div>
                            <span
                                className="entity-type-label"
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.coachingEntityTypeId === 10) {
                                        navigate(`${GAMEPLAN}`, { replace: true });
                                    }
                                    else if (getNavigationTab(item.coachingEntityTypeId) === CoachingTabs.MyTODos) {
                                        navigate(`${COACHING}/${CoachingTabs.MyTODos}/${type === 'myteam' ? 2 : 1}/${item.coachingToDoId || 1
                                            }/${item.entityDate || 1}`, { replace: true }
                                        );
                                    }
                                    else {
                                        navigate(
                                            `${COACHING}/${getNavigationTab(item.coachingEntityTypeId)}/${type === 'myteam' ? 2 : 1}/${item.coachingEntityId || 1
                                            }/${item.entityDate || item.coachingEntityTypeId}`, { replace: true }
                                        );
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (item.coachingEntityTypeId === 10) {
                                            navigate(`${GAMEPLAN}`, { replace: true });
                                        }
                                        else if (getNavigationTab(item.coachingEntityTypeId) === CoachingTabs.MyTODos) {
                                            navigate(`${COACHING}/${CoachingTabs.MyTODos}/${type === 'myteam' ? 2 : 1}/${item.coachingToDoId || 1
                                                }/${item.entityDate || 1}`, { replace: true }
                                            );
                                        }
                                        else {
                                            navigate(
                                                `${COACHING}/${getNavigationTab(item.coachingEntityTypeId)}/${type === 'myteam' ? 2 : 1}/${item.coachingEntityId || 1
                                                }/${item.entityDate || item.coachingEntityTypeId}`, { replace: true }
                                            );
                                        }
                                    }
                                }}
                            >
                                {getEntityTypeName(item.coachingEntityTypeId) === 'General' || item.coachingEntityTypeId === 14
                                    ? 'Go To MyToDos'
                                    : `Go To ${getEntityTypeName(item.coachingEntityTypeId)}`}
                            </span>
                        </div>
                    ) : (''
                    )}
                </div>
            </div>
        ));

    const renderNoData = () => (
        <div className="no-data-container">
            <span className="no-data-text">No Data Available</span>
            <div className="no-data-illustration">
                <img src={arrow1} alt="Arrow 1" className="arrow arrow1" />
                <img src={arrow2} alt="Arrow 2" className="arrow arrow2" />
                <img src={bullseye} alt="Bullseye" className="bullseye" />
            </div>
        </div>
    );

    const renderContent = () => {
        if (toDosFetching) return <LoadingSpinner />;
        if (toDoList.length > 0) return renderToDos();
        return renderNoData();
    };

    return (
        <div className="todo-list-box">
            <div className="todo-body">{renderContent()}</div>
            <div className="todo-footer">
                {/* <button className="create-button" onClick={handleCreate}>
                    {toDoList.length > 0 ? '+ Create To Do' : '+ Add New Task'}
                </button> */}

                <Button
                    label={toDoList.length > 0 ? 'Create To Do' : 'Add New Task'}
                    icon="pi pi-plus-circle"
                    className="btn-primary w-100"
                    onClick={handleCreate}
                />
            </div>
        </div>
    );
};
